import { fetch, ProxyAgent } from 'undici'

import { URL } from 'node:url'
import { SDK_VERSION } from './version'

function getProxyAgent(envVar: string) {
  const proxyURI = process.env[envVar] || process.env[envVar.toUpperCase()]
  return proxyURI ? new ProxyAgent(proxyURI) : false
}

const httpProxy =
  getProxyAgent('http_proxy_license_server') || getProxyAgent('http_proxy')

const httpsProxy =
  getProxyAgent('https_proxy_license_server') || getProxyAgent('https_proxy')

const proxyAgents = {
  ...(httpProxy ? { 'http:': httpProxy } : {}),
  ...(httpsProxy ? { 'https:': httpsProxy } : {}),
}

const noProxyRules = (
  (process.env['no_proxy'] || process.env['NO_PROXY']) ??
  ''
)
  .split(',')
  .map((rule) => rule.trim())
  .filter((rule) => rule.length > 0)

const shouldUseProxy = (url: URL) => {
  const { host, protocol } = url

  if (protocol === 'http:' && !httpProxy) {
    return false
  }

  if (protocol === 'https:' && !httpsProxy) {
    return false
  }

  if (noProxyRules.some((rule) => host.endsWith(rule))) {
    return false
  }

  return true
}

export async function postRequest<T>(
  url: string,
  data: Record<string, unknown>,
  options: {
    timeoutInMs?: number
    productIdentifier?: string
  } = {}
) {
  const targetUrl = new URL(url)

  let proxyAgent: ProxyAgent | undefined

  if (shouldUseProxy(targetUrl)) {
    proxyAgent = proxyAgents[targetUrl.protocol as 'http:' | 'https:']
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': `License-SDK/${SDK_VERSION} (Node.js ${
          process.version
        }; ${process.platform}) ${options.productIdentifier ?? ''}`,
      },
      signal: AbortSignal.timeout(options.timeoutInMs ?? 30_000),
      ...(proxyAgent ? { dispatcher: proxyAgent } : {}),
    })

    return {
      status: response.status,
      data: (await response.json()) as T,
    }
  } catch (err) {
    const error = new Error('Connection Error: ' + (err as any).message)
    ;(error as any).cause = err
    throw error
  }
}

export default {
  postRequest,
}
